/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';

import { getActivityLogWhen } from '@/Client/Lib';
import type { ActivityLogTableStyles } from './activityLogTableStyles';
import type { ActivityLogTableColumn, ActivityLogTableEntry, ActivityLogTableSection } from './activityLogTableTypes';
import { ActivityLogTableWho } from './ActivityLogTableWho';

const SectionDate = ({ at, styles }: { at: Date; styles: ActivityLogTableStyles }) => {
  const when = getActivityLogWhen(at);
  return (
    <span css={styles.day}>
      <span css={styles.month}>{when.month}</span> {when.day}
      <sup css={styles.ordinal}>{when.ordinal}</sup>
    </span>
  );
};

/** What a section's heading shows in each of the table's own columns: its date, mark, who, and title; nothing elsewhere. */
const getSectionCellContent = (columnKey: string, section: ActivityLogTableSection, styles: ActivityLogTableStyles): ReactNode => {
  switch (columnKey) {
    case 'when':
      return <SectionDate at={section.at} styles={styles} />;
    case 'mark':
      return section.mark ? <span css={styles.sectionMark}>{section.mark}</span> : null;
    case 'who':
      return <ActivityLogTableWho userId={section.actorUserId} />;
    case 'what':
      return (
        <div css={styles.sectionTitleRow}>
          <div css={styles.sectionTitle}>{section.title}</div>
          {section.aside !== undefined && <div css={styles.sectionTitle} data-testid={'activity-log-table-section-aside'}>{section.aside}</div>}
        </div>
      );
    default:
      return null;
  }
};

/**
 * A section's heading: a half-height band, ruled above and below edge to edge, laid out like an
 * entry: its date (no time) in the date slot, who, and its title, bold and a size up, in the "what"
 * slot, each cell aligned as the entries' are. Its mark sits off the timeline rail.
 */
export const ActivityLogTableSectionHeader = <Entry extends ActivityLogTableEntry>({ section, columns, styles }: {
  section: ActivityLogTableSection;
  columns: readonly ActivityLogTableColumn<Entry>[];
  styles: ActivityLogTableStyles;
}) => (
  <tr data-testid={'activity-log-table-section'}>
    {columns.map((column, columnIndex) => (
      <td
        key={column.key}
        data-testid={`activity-log-table-cell-${column.key}`}
        css={[
          styles.cell,
          column.key !== 'mark' && column.cellStyle?.(styles, { isFirst: false, isLast: false }),
          styles.sectionHeaderCell,
          columnIndex === 0 && styles.firstSectionHeaderCell,
          columnIndex === columns.length - 1 && [styles.lastColumnCell, styles.lastSectionHeaderCell],
        ]}
      >
        {getSectionCellContent(column.key, section, styles)}
      </td>
    ))}
  </tr>
);
